import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Chip,
  Grid,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OnboardingDialogsContext, Page } from "contexts/OnboardingDialogs";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { UserIntegrationsContext } from "contexts/UserIntegrations";
import {
  Dispatch,
  RefObject,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { OrganizationProfile } from "schemas/dashboard";
import { CustomEvent, Map } from "schemas/functions";

import StyledLargeButton from "components/Styled/Button/StyledLargeButton";
import StyledSelect from "components/Styled/Select";
import {
  SocialMediaLinks,
  sanitizeInput,
} from "features/Auth/Onboarding/AboutForm/helpers";
import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";
import GoogleConnect from "features/Settings/GoogleConnect";
import { fetcherAuth } from "utils/api";
import { isValidHttpUrl } from "utils/string";
import { trackEvent } from "utils/tracking";

import { ChipsWithOther } from "./components/ChipsWithOther";
import { GeneratingScreen } from "./components/GeneratingScreen";
import { PasteSamples } from "./components/PasteSamples";
import { ProfileFieldsList } from "./components/ProfileFieldsList";
import {
  formatAboutYouSummary,
  formatBestWorkSummary,
  iconForAboutYou,
  iconForBestWork,
} from "./helpers";
import {
  ABOUT_YOU_OPTIONS,
  BEST_WORK_OPTIONS,
  METHOD_OPTIONS,
  NICHES,
  Step2Data,
  TONE_PRESETS,
  WRITING_STYLE_OPTIONS,
  WritingStyleSource,
} from "./schema";
import styles from "./styles";

interface Props {
  value: Step2Data;
  onChange: (v: Step2Data) => void;
  onBack: () => void;
  onNext: () => void;
  templateName: string;
  contentType: TemplateContentType | null;
  setOutreachTemplate: Dispatch<SetStateAction<Template>>;
  setFollowUpTemplate: Dispatch<SetStateAction<Template>>;
  onLoadingChange?: (isLoading: boolean) => void;
}

export function Step2Angle({
  value,
  templateName,
  onChange,
  onBack,
  onNext,
  contentType,
  setOutreachTemplate,
  setFollowUpTemplate,
  onLoadingChange,
}: Props) {
  const { templates } = useContext(OutreachTemplatesContext);
  const { emailHealth, userIntegration } = useContext(UserIntegrationsContext);
  const { currentOrg, profile, setProfile } = useContext(
    OrganizationUserContext,
  );
  const { setPage } = useContext(OnboardingDialogsContext);
  const { setErrorAlert } = useContext(AlertContext);
  const { getToken } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const selectedNiches = value.niches ?? [];

  const toggleNiche = (v: string) => {
    const selected = !selectedNiches.includes(v);
    const next = selected
      ? [...selectedNiches, v]
      : selectedNiches.filter((n) => n !== v);
    trackEvent("Template - Pick Niche", {
      Niche: v,
      Selected: selected,
    });
    onChange({ ...value, niches: next });
  };

  const isUserFirstTemplate = templates?.length === 0;

  const writingStyleSource: WritingStyleSource =
    value.writingStyleSource ?? "gmail";

  const handleWritingStyleSourceChange = (source: WritingStyleSource) => {
    trackEvent("Template - Pick Writing Style Source", {
      "Writing Style Source": source,
    });
    onChange({ ...value, writingStyleSource: source });
  };
  const [aboutYouSectionExpanded, setAboutYouSectionExpanded] =
    useState<boolean>(false);
  const [bestWorkSectionExpanded, setBestWorkSectionExpanded] =
    useState<boolean>(false);
  const aboutYouSectionRef = useRef<HTMLDivElement>(null);
  const bestWorkSectionRef = useRef<HTMLDivElement>(null);
  const [pendingSample, setPendingSample] = useState("");
  const [socialLinks, setSocialLinks] = useState<SocialMediaLinks[]>([]);
  const [socialLinksValidation, setSocialLinksValidation] = useState<{
    [key in string]: string | undefined;
  }>({});
  const [error, setError] = useState<Map>({
    field: "",
    errorMessage: "",
  });
  const [loading, setLoading] = useState({
    portfolio: false,
    emails: false,
    socialMedia: false,
    saveProfile: false,
    generateTemplate: false,
  });
  const [stepErrors, setStepErrors] = useState<{
    portfolio?: string;
    emails?: string;
    socialMedia?: string;
    generateTemplate?: string;
  }>({});
  const [toneExamples, setToneExamples] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!currentOrg?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg.id}/templates/tone-examples`,
        );
        if (!cancelled && res) setToneExamples(res);
      } catch (err) {
        setErrorAlert(err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentOrg?.id, getToken, setErrorAlert]);

  const useCollapseOnOutsideClick = (
    ref: RefObject<HTMLElement>,
    active: boolean,
    collapse: () => void,
  ) => {
    useEffect(() => {
      if (!active) return;
      const handleOutsideClick = (event: MouseEvent) => {
        const target = event.target as Element | null;
        if (!target) return;
        // MUI Select/Autocomplete render their popups in a portal at document.body,
        // so clicks on dropdown items aren't contained by the section ref. Treat
        // those as inside-the-section clicks.
        if (
          target.closest?.(".MuiPopover-root, .MuiModal-root, .MuiPopper-root")
        ) {
          return;
        }
        if (ref.current && !ref.current.contains(target as Node)) {
          collapse();
        }
      };
      document.addEventListener("mousedown", handleOutsideClick);
      return () =>
        document.removeEventListener("mousedown", handleOutsideClick);
    }, [active, ref, collapse]);
  };

  useCollapseOnOutsideClick(
    aboutYouSectionRef,
    aboutYouSectionExpanded && !!value.aboutYouItem,
    () => setAboutYouSectionExpanded(false),
  );
  useCollapseOnOutsideClick(
    bestWorkSectionRef,
    bestWorkSectionExpanded && !!value.bestWorkItem,
    () => setBestWorkSectionExpanded(false),
  );

  const portfolioUrl = profile?.portfolioUrl?.trim() || "";
  const hasIntroduction = !!profile?.introduction?.trim();
  const showAboutYouSection =
    isUserFirstTemplate || (!portfolioUrl && !hasIntroduction);

  const generatePitch = async () => {
    if (value.method === "ai") {
      if (portfolioUrl.length > 0 && !isValidHttpUrl(portfolioUrl)) {
        setError({
          field: "portfolioUrl",
          errorMessage: "Please enter a valid URL (https://www.example.com)",
        });
        return;
      }

      // Fold any in-progress paste into the persisted samples so the user
      // doesn't lose work for forgetting to click "Add sample".
      const trimmedPending = pendingSample.trim();
      const existingSamples = profile?.writingStyleSamples ?? [];
      const effectiveSamples = trimmedPending
        ? [...existingSamples, trimmedPending]
        : existingSamples;
      if (trimmedPending) {
        setProfile(
          (prev) =>
            ({
              ...(prev ?? {}),
              writingStyleSamples: effectiveSamples,
            }) as OrganizationProfile,
        );
        setPendingSample("");
      }

      if (writingStyleSource === "paste" && effectiveSamples.length === 0) {
        setErrorAlert(
          new Error("Please add at least one writing sample or choose a tone."),
        );
        return;
      }

      if (writingStyleSource === "gmail" && !emailHealth) {
        setErrorAlert(
          new Error(
            "Please connect your Gmail to use this option or choose a different tone source.",
          ),
        );
        return;
      }

      if (
        showAboutYouSection &&
        portfolioUrl.length === 0 &&
        !value.aboutYouItem
      ) {
        setErrorAlert(
          new Error(
            "Please fill out the tone section and tell us about yourself",
          ),
        );
        return;
      }
      await saveProfile();
      await generateEmailFromAllInfoGiven();
    } else {
      setPage(Page.fromScratch);
    }
  };

  const parsePortfolio = async () => {
    if (!portfolioUrl || (portfolioUrl && !isValidHttpUrl(portfolioUrl)))
      return null;

    setLoading((prev) => ({ ...prev, portfolio: true }));
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/templates/parse-portfolio`,
        "POST",
        {},
        {
          portfolioUrl,
        },
      );
      if (res?.error) {
        setStepErrors((prev) => ({ ...prev, portfolio: res.error }));
      }
    } catch (err) {
      setStepErrors((prev) => ({
        ...prev,
        portfolio: err instanceof Error ? err.message : String(err),
      }));
    } finally {
      setLoading((prev) => ({ ...prev, portfolio: false }));
    }
  };

  const parseSocialMediaAccount = async () => {
    const instagramUsername = sanitizeInput(profile?.instagramUrl);
    if (!instagramUsername) return null;

    setLoading((prev) => ({ ...prev, socialMedia: true }));

    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/templates/parse-instagram`,
        "POST",
        {},
        {
          instagramUsername: instagramUsername,
        },
      );
      if (res?.error) {
        setStepErrors((prev) => ({ ...prev, socialMedia: res.error }));
      }
    } catch (err) {
      setStepErrors((prev) => ({
        ...prev,
        socialMedia: err instanceof Error ? err.message : String(err),
      }));
    } finally {
      setLoading((prev) => ({ ...prev, socialMedia: false }));
    }
  };

  const parseOutreachEmail = async () => {
    setLoading((prev) => ({ ...prev, emails: true }));
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/templates/parse-past-emails`,
        "POST",
      );
      if (res?.error) {
        setStepErrors((prev) => ({ ...prev, emails: res.error }));
      }
    } catch (err) {
      setStepErrors((prev) => ({
        ...prev,
        emails: err instanceof Error ? err.message : String(err),
      }));
    } finally {
      setLoading((prev) => ({ ...prev, emails: false }));
    }
  };

  const generateEmailFromAllInfoGiven = async () => {
    const portfolioTask = portfolioUrl
      ? parsePortfolio()
      : Promise.resolve(null);

    const emailsTask =
      writingStyleSource === "gmail"
        ? parseOutreachEmail()
        : Promise.resolve(null);

    const socialMediaTask =
      profile?.instagramUrl || profile?.tiktokUrl
        ? parseSocialMediaAccount()
        : Promise.resolve(null);

    await Promise.allSettled([portfolioTask, emailsTask, socialMediaTask]);

    await generateTemplate();
  };

  const generateTemplate = async () => {
    trackEvent("Template Created", {
      Method: value.method,
      Source: writingStyleSource,
      "Best Work": value.bestWorkItem,
      "About You": value.aboutYouItem,
      "Niches Count": selectedNiches.length,
    });

    setLoading((prev) => ({ ...prev, generateTemplate: true }));

    const tone =
      writingStyleSource === "tone" && profile?.writingStyleTone
        ? profile.writingStyleTone
        : undefined;
    try {
      const { outreach, followup } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/templates/generate`,
        "POST",
        {},
        {
          templateName,
          contentType,
          bestWorkItem: value.bestWorkItem,
          niches: selectedNiches,
          tone,
        },
      );
      if (outreach) {
        setOutreachTemplate(outreach);
      }
      if (followup) {
        setFollowUpTemplate(followup);
      }
      onNext();
    } catch (err) {
      setErrorAlert(err);
    } finally {
      setLoading((prev) => ({ ...prev, generateTemplate: false }));
    }
  };

  const saveProfile = async () => {
    if (!profile) {
      return;
    }
    setLoading((prev) => ({ ...prev, saveProfile: true }));
    try {
      const { organizationProfile } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/settings`,
        "PUT",
        {},
        profile,
      );
      setProfile(organizationProfile);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading((prev) => ({ ...prev, saveProfile: false }));
    }
  };

  const isGenerating = Object.values(loading).some(Boolean);

  useEffect(() => {
    onLoadingChange?.(isGenerating);
  }, [isGenerating, onLoadingChange]);

  const expansionInitializedRef = useRef(false);
  useEffect(() => {
    if (expansionInitializedRef.current) return;
    if (!profile) return;
    expansionInitializedRef.current = true;

    const aboutYouHasValue = (() => {
      if (value.aboutYouItem === "portfolioUrl")
        return !!profile.portfolioUrl?.trim();
      if (value.aboutYouItem === "introduction")
        return !!profile.introduction?.trim();
      return false;
    })();

    const bestWorkHasValue = (() => {
      const item = value.bestWorkItem;
      if (item === "socialMediaPage") {
        return !!(
          profile.instagramUrl?.trim() ||
          profile.tiktokUrl?.trim() ||
          profile.youtubeUrl?.trim() ||
          profile.twitchUrl?.trim()
        );
      }
      const profileMap = profile as unknown as Record<
        string,
        string | undefined
      >;
      return !!profileMap[item]?.trim();
    })();

    setAboutYouSectionExpanded(!aboutYouHasValue);
    setBestWorkSectionExpanded(!bestWorkHasValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, emailHealth]);

  if (isGenerating) {
    return (
      <GeneratingScreen
        loading={loading}
        errors={stepErrors}
        showPortfolio={!!portfolioUrl}
        showEmails={writingStyleSource === "gmail"}
        showSocialMedia={!!profile?.instagramUrl}
      />
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        How do you want to write your pitch?
      </Typography>

      <Grid container spacing={2} sx={styles.cardsGrid}>
        {METHOD_OPTIONS.map((option) => {
          const active = value.method === option.id;
          return (
            <Grid item xs={6} key={option.id}>
              <Box
                onClick={() => {
                  trackEvent("Template - Pick Method", {
                    Method: option.id,
                  });
                  onChange({ ...value, method: option.id });
                }}
                sx={styles.methodCard(option, active)}
              >
                <Box sx={styles.methodIconWrapper(option)}>
                  <Box
                    component="i"
                    className={option.icon}
                    sx={styles.methodIcon(option)}
                  />
                </Box>

                <Box sx={styles.methodCardContent}>
                  <Typography sx={styles.methodCardLabel(option)}>
                    {option.label}
                  </Typography>
                  <Typography sx={styles.methodCardSub(option)}>
                    {option.sub}
                  </Typography>
                </Box>

                <Box
                  component="i"
                  className={
                    active ? "fa-solid fa-circle-check" : "fa-regular fa-circle"
                  }
                  sx={styles.methodCardCheck(option, active)}
                />
              </Box>
            </Grid>
          );
        })}
      </Grid>

      {value.method === "ai" && (
        <>
          <Typography sx={styles.sectionTitle}>Tone *</Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              gap: 1.5,
              mb: 2,
            }}
          >
            <Box sx={{ ...styles.bestWorkSelectCard, flex: 1 }}>
              <StyledSelect
                value={writingStyleSource}
                variant="standard"
                disableUnderline
                styles={styles.bestWorkSelect}
                renderValue={(selected: WritingStyleSource) =>
                  WRITING_STYLE_OPTIONS.find((o) => o.id === selected)?.label ??
                  ""
                }
                onChange={(e: CustomEvent) =>
                  handleWritingStyleSourceChange(
                    e.target.value as WritingStyleSource,
                  )
                }
              >
                {WRITING_STYLE_OPTIONS.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    <Box sx={styles.bestWorkOption}>
                      <Typography sx={styles.bestWorkOptionLabel}>
                        {option.label}
                      </Typography>
                      <Typography sx={styles.bestWorkOptionSub}>
                        {option.sub}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </StyledSelect>
              {WRITING_STYLE_OPTIONS.find((o) => o.id === writingStyleSource)
                ?.recommended && (
                <Box sx={styles.bestWorkRecommendedBadge}>Recommended</Box>
              )}
            </Box>

            {writingStyleSource === "gmail" &&
              (emailHealth ? (
                <Chip
                  label={
                    userIntegration?.email
                      ? "Gmail successfully connected"
                      : "Not connected"
                  }
                  icon={
                    <Box
                      sx={styles.connectionChipIcon}
                      component="i"
                      className={
                        userIntegration?.email
                          ? "fa-regular fa-check"
                          : "fa-solid fa-warning"
                      }
                    />
                  }
                  sx={styles.connectionChip(!!userIntegration?.email)}
                />
              ) : (
                <GoogleConnect styles={styles.compactGoogleConnectButton} />
              ))}
          </Box>

          {writingStyleSource === "paste" && (
            <Box sx={{ mb: 2 }}>
              <PasteSamples
                pendingSample={pendingSample}
                setPendingSample={setPendingSample}
                samples={profile?.writingStyleSamples ?? []}
                onSamplesChange={(next) =>
                  setProfile(
                    (prev) =>
                      ({
                        ...(prev ?? {}),
                        writingStyleSamples: next,
                      }) as OrganizationProfile,
                  )
                }
              />
            </Box>
          )}

          {writingStyleSource === "tone" && (
            <Box sx={{ mb: 2 }}>
              <Box sx={styles.chipsWrapper}>
                {TONE_PRESETS.map((tone) => {
                  const selected = profile?.writingStyleTone === tone.id;
                  const example = toneExamples[tone.id];
                  return (
                    <Box
                      key={tone.id}
                      component="button"
                      type="button"
                      onClick={() => {
                        trackEvent("Template - Pick Tone Preset", {
                          Tone: tone.id,
                          Selected: !selected,
                        });
                        setProfile(
                          (prev) =>
                            ({
                              ...(prev ?? {}),
                              writingStyleTone: selected ? "" : tone.id,
                            }) as OrganizationProfile,
                        );
                      }}
                      sx={styles.tonePresetChip(selected)}
                    >
                      <Typography sx={styles.tonePresetLabel(selected)}>
                        <Box component="span" sx={styles.tonePresetEmoji}>
                          {tone.emoji}
                        </Box>
                        {tone.label}
                      </Typography>
                      {example && (
                        <>
                          <Typography
                            sx={styles.tonePresetExampleLabel(selected)}
                          >
                            Example
                          </Typography>
                          <Typography sx={styles.tonePresetExample(selected)}>
                            “{example}”
                          </Typography>
                        </>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {showAboutYouSection && (
            <>
              <Typography sx={styles.sectionTitle}>
                Tell us about yourself *
              </Typography>
              {!aboutYouSectionExpanded && value.aboutYouItem ? (
                <Box sx={styles.checklistContainer}>
                  <Box
                    onClick={() => setAboutYouSectionExpanded(true)}
                    sx={styles.sourceCard(true)}
                  >
                    <Box sx={styles.sourceCardHeader}>
                      <Box sx={styles.sourceCardIcon}>
                        <Box
                          component="i"
                          className={iconForAboutYou(value.aboutYouItem)}
                        />
                      </Box>
                      <Typography sx={styles.sourceCardLabel}>
                        {formatAboutYouSummary(value.aboutYouItem, profile)}
                      </Typography>
                      <Box
                        component="i"
                        className="fa-solid fa-circle-check"
                        sx={{ ml: "auto", color: "#004537", fontSize: 16 }}
                      />
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box ref={aboutYouSectionRef}>
                  <ProfileFieldsList
                    items={[value.aboutYouItem]}
                    options={ABOUT_YOU_OPTIONS}
                    setItems={(next) =>
                      onChange({
                        ...value,
                        aboutYouItem: next[0] ?? value.aboutYouItem,
                      })
                    }
                    error={error}
                    setError={setError}
                    socialLinks={socialLinks}
                    setSocialLinks={setSocialLinks}
                    socialLinksValidation={socialLinksValidation}
                    setSocialLinksValidation={setSocialLinksValidation}
                  />
                </Box>
              )}
            </>
          )}

          <Typography sx={styles.sectionTitle}>Show your best work</Typography>
          <Typography variant="caption" gutterBottom>
            We'll highlight this in your template
          </Typography>
          {!bestWorkSectionExpanded && value.bestWorkItem ? (
            <Box sx={styles.checklistContainer}>
              <Box
                onClick={() => setBestWorkSectionExpanded(true)}
                sx={styles.sourceCard(true)}
              >
                <Box sx={styles.sourceCardHeader}>
                  <Box sx={styles.sourceCardIcon}>
                    <Box
                      component="i"
                      className={iconForBestWork(value.bestWorkItem)}
                    />
                  </Box>
                  <Typography sx={styles.sourceCardLabel}>
                    {formatBestWorkSummary(value.bestWorkItem)}
                  </Typography>
                  <Box
                    component="i"
                    className="fa-solid fa-circle-check"
                    sx={{ ml: "auto", color: "#004537", fontSize: 16 }}
                  />
                </Box>
              </Box>
            </Box>
          ) : (
            <Box ref={bestWorkSectionRef}>
              <ProfileFieldsList
                items={[value.bestWorkItem]}
                options={BEST_WORK_OPTIONS}
                setItems={(next) => {
                  const nextItem = next[0] ?? value.bestWorkItem;
                  if (nextItem !== value.bestWorkItem) {
                    trackEvent("Template - Pick Best Work", {
                      "Best Work Item": nextItem,
                    });
                  }
                  onChange({ ...value, bestWorkItem: nextItem });
                }}
                error={error}
                setError={setError}
                socialLinks={socialLinks}
                setSocialLinks={setSocialLinks}
                socialLinksValidation={socialLinksValidation}
                setSocialLinksValidation={setSocialLinksValidation}
              />
            </Box>
          )}

          {!isUserFirstTemplate && (
            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{
                  fontSize: isMobile ? 14 : 15,
                  fontWeight: 600,
                  color: "#0A0A0A",
                  display: "block",
                  mt: isMobile ? 2.5 : 3,
                  mb: 1,
                }}
              >
                Write a template for these niches (optional)
              </Typography>
              <ChipsWithOther
                values={NICHES}
                selected={selectedNiches}
                onToggle={toggleNiche}
                otherPlaceholder="Add your niche"
              />
            </Box>
          )}
        </>
      )}

      <Box sx={styles.actionsRowSplit}>
        <StyledLargeButton variant="outlined" onClick={onBack}>
          Back
        </StyledLargeButton>
        <StyledLargeButton
          variant="contained"
          disableElevation
          onClick={generatePitch}
        >
          {value.method === "ai" ? "Generate pitch" : "Continue"}
        </StyledLargeButton>
      </Box>
    </Box>
  );
}
